import { SEO } from "@/components/seo";
import { siteConfig } from "@/config/website.config";
import { broker } from "@/config/broker.config";

export function PrivacyPolicyPage() {
  const lastUpdated = "2 January 2026";

  return (
    <>
      <SEO
        title="Privacy Policy"
        description="Learn how Path Financial Solutions collects, uses, and protects your personal information in accordance with Australian privacy laws."
        url="/privacy-policy"
      />

      {/* Hero */}
      <section className="bg-cream py-16 md:py-20">
        <div className="max-w-4xl mx-auto px-4 md:px-6">
          <p className="section-label mb-4">Legal</p>
          <h1 className="font-serif text-4xl md:text-5xl mb-4">Privacy Policy</h1>
          <p className="text-muted-foreground">Last updated: {lastUpdated}</p>
        </div>
      </section>

      {/* Content */}
      <section className="py-16 md:py-20">
        <div className="max-w-4xl mx-auto px-4 md:px-6">
          <div className="prose prose-lg max-w-none">
            {/* Introduction */}
            <div className="mb-12">
              <h2 className="font-serif text-2xl mb-4">1. Introduction</h2>
              <p className="text-muted-foreground mb-4">
                In this Privacy Policy, the expressions we, us and our includes us and our aggregator
                Australian Finance Group Ltd ABN 11 066 385 822 ('AFG') Australian Credit Licence 389087,
                its subsidiaries and Path Financial Solutions ABN 97193515627.
              </p>
              <p className="text-muted-foreground">
                By using our services, you consent to the collection and use of your information
                as described in this policy.
              </p>
            </div>

            {/* Information We Collect */}
            <div className="mb-12">
              <h2 className="font-serif text-2xl mb-4">2. Information We Collect</h2>
              <p className="text-muted-foreground mb-4">
                To provide mortgage broking services, we collect the following types of personal information:
              </p>

              <h3 className="font-semibold text-lg mb-3">Identity Information</h3>
              <ul className="list-disc pl-6 text-muted-foreground mb-4 space-y-1">
                <li>Full name, date of birth, and gender</li>
                <li>Residential address and contact details</li>
                <li>Driver's licence, passport, or other identification documents</li>
                <li>Medicare number (for identity verification)</li>
              </ul>

              <h3 className="font-semibold text-lg mb-3">Financial Information</h3>
              <ul className="list-disc pl-6 text-muted-foreground mb-4 space-y-1">
                <li>Employment details and income information</li>
                <li>Bank statements and transaction history</li>
                <li>Existing loans, debts, and credit card limits</li>
                <li>Assets including property, vehicles, and investments</li>
                <li>Tax returns and notices of assessment</li>
                <li>Superannuation and investment statements</li>
              </ul>

              <h3 className="font-semibold text-lg mb-3">Credit Information</h3>
              <ul className="list-disc pl-6 text-muted-foreground mb-4 space-y-1">
                <li>Credit reports from credit reporting bodies</li>
                <li>Credit scores and credit history</li>
                <li>Details of previous credit applications</li>
                <li>Repayment history on existing credit products</li>
              </ul>

              <h3 className="font-semibold text-lg mb-3">Sensitive Information</h3>
              <p className="text-muted-foreground">
                We may collect sensitive information such as health information only where it is
                relevant to your loan application (e.g., income protection insurance requirements)
                and with your explicit consent.
              </p>
            </div>

            {/* How We Collect Information */}
            <div className="mb-12">
              <h2 className="font-serif text-2xl mb-4">3. How We Collect Information</h2>
              <p className="text-muted-foreground mb-4">We collect personal information:</p>
              <ul className="list-disc pl-6 text-muted-foreground space-y-1">
                <li>Directly from you when you complete application forms or communicate with us</li>
                <li>From your employer, accountant, or financial adviser with your consent</li>
                <li>From credit reporting bodies (Equifax, Experian, illion)</li>
                <li>From lenders in relation to your loan applications</li>
                <li>From government bodies such as the ATO</li>
                <li>Through our website when you submit enquiry forms or use our calculators</li>
                <li>From publicly available sources</li>
              </ul>
            </div>

            {/* How We Use Your Information */}
            <div className="mb-12">
              <h2 className="font-serif text-2xl mb-4">4. How We Use Your Information</h2>
              <p className="text-muted-foreground mb-4">We use your personal information to:</p>
              <ul className="list-disc pl-6 text-muted-foreground space-y-1">
                <li>Assess your financial situation and borrowing capacity</li>
                <li>Identify suitable loan products from our panel of lenders</li>
                <li>Submit loan applications on your behalf</li>
                <li>Communicate with you about your application status</li>
                <li>Provide ongoing loan management services</li>
                <li>Comply with our legal and regulatory obligations</li>
                <li>Conduct internal business analysis and improvement</li>
                <li>Send you relevant updates about rates and products (with your consent)</li>
              </ul>
            </div>

            {/* Disclosure of Information */}
            <div className="mb-12">
              <h2 className="font-serif text-2xl mb-4">5. Disclosure of Your Information</h2>
              <p className="text-muted-foreground mb-4">We may disclose your personal information to:</p>
              <ul className="list-disc pl-6 text-muted-foreground mb-4 space-y-1">
                <li>Lenders on our panel for the purpose of loan applications</li>
                <li>Our aggregator, {siteConfig.business.aclProvider}</li>
                <li>Credit reporting bodies</li>
                <li>Lenders' mortgage insurers</li>
                <li>Valuers and property information providers</li>
                <li>Your solicitor or conveyancer</li>
                <li>Your accountant or financial adviser</li>
                <li>Government bodies as required by law (e.g., AUSTRAC, ATO)</li>
                <li>Our professional advisers (lawyers, auditors)</li>
                <li>Third-party service providers who assist our business operations</li>
              </ul>
              <p className="text-muted-foreground">
                We will not sell your personal information to third parties for marketing purposes.
              </p>
            </div>

            {/* Credit Reporting */}
            <div className="mb-12">
              <h2 className="font-serif text-2xl mb-4">6. Credit Reporting</h2>
              <p className="text-muted-foreground mb-4">
                Under the <em>Privacy Act 1988</em>, we may disclose your credit information to
                credit reporting bodies. These bodies may include this information in reports
                provided to other credit providers.
              </p>
              <p className="text-muted-foreground mb-4">
                The credit reporting bodies we use include:
              </p>
              <ul className="list-disc pl-6 text-muted-foreground mb-4 space-y-1">
                <li>Equifax Australia - <a href="https://www.equifax.com.au" className="text-primary hover:underline">www.equifax.com.au</a></li>
                <li>Experian Australia - <a href="https://www.experian.com.au" className="text-primary hover:underline">www.experian.com.au</a></li>
                <li>illion - <a href="https://www.illion.com.au" className="text-primary hover:underline">www.illion.com.au</a></li>
              </ul>
              <p className="text-muted-foreground">
                You have the right to request access to your credit report and to request
                corrections if the information is inaccurate.
              </p>
            </div>

            {/* Data Security */}
            <div className="mb-12">
              <h2 className="font-serif text-2xl mb-4">7. Data Security</h2>
              <p className="text-muted-foreground mb-4">
                We take reasonable steps to protect your personal information from misuse,
                interference, loss, and unauthorised access, modification, or disclosure.
                Our security measures include:
              </p>
              <ul className="list-disc pl-6 text-muted-foreground space-y-1">
                <li>Secure, encrypted storage of electronic records</li>
                <li>Password protection and multi-factor authentication</li>
                <li>Secure physical storage of paper documents</li>
                <li>Staff training on privacy and data protection</li>
                <li>Regular security assessments and updates</li>
                <li>Secure destruction of information no longer required</li>
              </ul>
            </div>

            {/* Data Retention */}
            <div className="mb-12">
              <h2 className="font-serif text-2xl mb-4">8. Data Retention</h2>
              <p className="text-muted-foreground">
                We retain your personal information for as long as necessary to fulfil the purposes
                for which it was collected, and to comply with our legal obligations. Under the
                <em> National Consumer Credit Protection Act 2009</em>, we are required to retain
                certain records for a minimum of 7 years. After this period, we will securely
                destroy or de-identify your personal information.
              </p>
            </div>

            {/* Your Rights */}
            <div className="mb-12">
              <h2 className="font-serif text-2xl mb-4">9. Your Rights</h2>
              <p className="text-muted-foreground mb-4">Under the Privacy Act, you have the right to:</p>
              <ul className="list-disc pl-6 text-muted-foreground mb-4 space-y-1">
                <li>Request access to your personal information</li>
                <li>Request correction of inaccurate or outdated information</li>
                <li>Request deletion of your information (subject to legal requirements)</li>
                <li>Withdraw consent for direct marketing</li>
                <li>Make a complaint about a breach of your privacy</li>
                <li>Request information about overseas disclosures</li>
              </ul>
              <p className="text-muted-foreground">
                To exercise these rights, please contact us using the details below.
              </p>
            </div>

            {/* Cookies and Analytics */}
            <div className="mb-12">
              <h2 className="font-serif text-2xl mb-4">10. Cookies and Website Analytics</h2>
              <p className="text-muted-foreground mb-4">
                Our website uses cookies and similar technologies to improve your browsing
                experience and analyse website traffic. We may use:
              </p>
              <ul className="list-disc pl-6 text-muted-foreground mb-4 space-y-1">
                <li>Essential cookies for website functionality</li>
                <li>Analytics cookies to understand how visitors use our site</li>
                <li>Marketing cookies to deliver relevant advertisements</li>
              </ul>
              <p className="text-muted-foreground">
                You can manage your cookie preferences through your browser settings.
              </p>
            </div>

            {/* Changes to Policy */}
            <div className="mb-12">
              <h2 className="font-serif text-2xl mb-4">11. Changes to This Policy</h2>
              <p className="text-muted-foreground">
                We may update this Privacy Policy from time to time to reflect changes in our
                practices or legal requirements. We will notify you of any material changes by
                posting the updated policy on our website with a new "Last updated" date.
              </p>
            </div>

            {/* Complaints */}
            <div className="mb-12">
              <h2 className="font-serif text-2xl mb-4">12. Complaints</h2>
              <p className="text-muted-foreground mb-4">
                If you believe we have breached your privacy, you can lodge a complaint with us.
                We will investigate and respond within 30 days.
              </p>
              <p className="text-muted-foreground mb-4">
                If you are not satisfied with our response, you can lodge a complaint with the
                Office of the Australian Information Commissioner (OAIC):
              </p>
              <ul className="list-none text-muted-foreground space-y-1">
                <li>Website: <a href="https://www.oaic.gov.au" className="text-primary hover:underline">www.oaic.gov.au</a></li>
                <li>Phone: 1300 363 992</li>
                <li>Email: enquiries@oaic.gov.au</li>
              </ul>
            </div>

            {/* Contact */}
            <div className="bg-cream rounded-2xl p-8">
              <h2 className="font-serif text-2xl mb-4">Contact Us</h2>
              <p className="text-muted-foreground mb-4">
                For privacy-related enquiries or to exercise your rights, please contact:
              </p>
              <ul className="list-none text-muted-foreground space-y-2">
                <li><strong>Privacy Officer:</strong> {broker.name}</li>
                <li><strong>Business:</strong> {siteConfig.appName}</li>
                <li><strong>Email:</strong> <a href={`mailto:${broker.email}`} className="text-primary hover:underline">{broker.email}</a></li>
                <li><strong>Phone:</strong> <a href={`tel:${broker.phone.replace(/\s/g, "")}`} className="text-primary hover:underline">{broker.phone}</a></li>
              </ul>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
